"use client";

import _toConsumableArray from "/home/circleci/project/web-front/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "/home/circleci/project/web-front/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useCheckboxContext } from "./3YZMJGQP.js";
import { CheckboxCheckedContext } from "./EYKMH5G5.js";
import { useCommand } from "./ZHUQ44XA.js";
import { createElement, createHook, forwardRef } from "./7FZLUSKW.js";
import { useStoreState } from "./CTKPPFRS.js";
import { useEvent, useForceUpdate, useMergeRefs, useTagName, useWrapElement } from "./HNHAUIIY.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/checkbox/checkbox.tsx
import { disabledFromProps, removeUndefinedValues } from "@ariakit/core/utils/misc";
import { useEffect, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
var TagName = "input";
function setMixed(element, mixed) {
  if (mixed) {
    element.indeterminate = true;
  } else if (element.indeterminate) {
    element.indeterminate = false;
  }
}
function isNativeCheckbox(tagName, type) {
  return tagName === "input" && (!type || type === "checkbox");
}
function getPrimitiveValue(value) {
  if (Array.isArray(value)) {
    return value.toString();
  }
  return value;
}
var useCheckbox = createHook(function useCheckbox2(_a) {
  var _b = _a,
    store = _b.store,
    name = _b.name,
    valueProp = _b.value,
    checkedProp = _b.checked,
    defaultChecked = _b.defaultChecked,
    props = __objRest(_b, ["store", "name", "value", "checked", "defaultChecked"]);
  var context = useCheckboxContext();
  store = store || context;
  var _useState = useState(defaultChecked != null ? defaultChecked : false),
    _useState2 = _slicedToArray(_useState, 2),
    _checked = _useState2[0],
    setChecked = _useState2[1];
  var checked = useStoreState(store, function (state) {
    if (checkedProp !== void 0) return checkedProp;
    if ((state == null ? void 0 : state.value) === void 0) return _checked;
    if (valueProp != null) {
      if (Array.isArray(state.value)) {
        var primitiveValue = getPrimitiveValue(valueProp);
        return state.value.includes(primitiveValue);
      }
      return state.value === valueProp;
    }
    if (Array.isArray(state.value)) return false;
    if (typeof state.value === "boolean") return state.value;
    return false;
  });
  var ref = useRef(null);
  var tagName = useTagName(ref, TagName);
  var nativeCheckbox = isNativeCheckbox(tagName, props.type);
  var mixed = checked ? checked === "mixed" : void 0;
  var isChecked = checked === "mixed" ? false : checked;
  var disabled = disabledFromProps(props);
  var _useForceUpdate = useForceUpdate(),
    _useForceUpdate2 = _slicedToArray(_useForceUpdate, 2),
    propertyUpdated = _useForceUpdate2[0],
    schedulePropertyUpdate = _useForceUpdate2[1];
  useEffect(function () {
    var element = ref.current;
    if (!element) return;
    setMixed(element, mixed);
    if (nativeCheckbox) return;
    element.checked = isChecked;
    if (name !== void 0) {
      element.name = name;
    }
    if (valueProp !== void 0) {
      element.value = "".concat(valueProp);
    }
  }, [propertyUpdated, mixed, nativeCheckbox, isChecked, name, valueProp]);
  var onChangeProp = props.onChange;
  var onChange = useEvent(function (event) {
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    setMixed(event.currentTarget, mixed);
    if (!nativeCheckbox) {
      event.currentTarget.checked = !event.currentTarget.checked;
      schedulePropertyUpdate();
    }
    onChangeProp == null ? void 0 : onChangeProp(event);
    if (event.defaultPrevented) return;
    var elementChecked = event.currentTarget.checked;
    setChecked(elementChecked);
    store == null ? void 0 : store.setValue(function (prevValue) {
      if (valueProp == null) return elementChecked;
      var primitiveValue = getPrimitiveValue(valueProp);
      if (!Array.isArray(prevValue)) {
        return prevValue === primitiveValue ? false : primitiveValue;
      }
      if (elementChecked) {
        if (prevValue.includes(primitiveValue)) {
          return prevValue;
        }
        return [].concat(_toConsumableArray(prevValue), [primitiveValue]);
      }
      return prevValue.filter(function (v) {
        return v !== primitiveValue;
      });
    });
  });
  var onClickProp = props.onClick;
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (nativeCheckbox) return;
    onChange(event);
  });
  props = useWrapElement(props, function (element) {
    return /* @__PURE__ */jsx(CheckboxCheckedContext.Provider, {
      value: isChecked,
      children: element
    });
  }, [isChecked]);
  props = __spreadProps(__spreadValues({
    role: !nativeCheckbox ? "checkbox" : void 0,
    type: nativeCheckbox ? "checkbox" : void 0,
    "aria-checked": checked
  }, props), {
    ref: useMergeRefs(ref, props.ref),
    onChange: onChange,
    onClick: onClick
  });
  props = useCommand(__spreadValues({
    clickOnEnter: !nativeCheckbox
  }, props));
  return removeUndefinedValues(__spreadValues({
    name: nativeCheckbox ? name : void 0,
    value: nativeCheckbox ? valueProp : void 0,
    checked: isChecked
  }, props));
});
var Checkbox = forwardRef(function Checkbox2(props) {
  var htmlProps = useCheckbox(props);
  return createElement(TagName, htmlProps);
});
export { useCheckbox, Checkbox };